import { forwardRef, ForwardedRef, useState } from 'react';
import { BUTTON_VARIANTS } from 'ui/button';
import { Edit16 } from 'ui/icons/action/Edit16';
import { useKeyPress } from 'utils/hooks/useKeyPress';
import {
  InputContainer,
  NameDisplayArea,
  NameDisplayPlaceholder,
  StyledButtonArea,
  StyledFauxButtonArea,
} from './InlineEdit.styled';
import { EDIT_INPUT_TEST_ID, EDIT_BUTTON_TEST_ID, FAUX_EDIT_BUTTON_TEST_ID } from './constants';
import type { InlineEditProps, FauxInlineEditProps } from './types';
import { CharacterCountTooltip } from './CharacterCountTooltip';
import { InlineEditAreaInput } from './InlineEditAreaInput';

export const InlineEditArea = forwardRef(
  (
    {
      EmptyTrigger,
      hasError,
      id = 'uiInlineEdit',
      isRequired = false,
      maxLength,
      onBlur,
      onChange,
      onReset,
      placeholder = '',
      showFocused = false,
      value = '',
      variant = 'default',
    }: InlineEditProps,
    ref: ForwardedRef<HTMLTextAreaElement>,
  ) => {
    const [isEditMode, setIsEditMode] = useState(false);

    const handleBlur = () => {
      if (!value?.trim() && isRequired) {
        onReset?.();
      } else {
        onBlur?.();
      }
      setIsEditMode(false);
    };

    useKeyPress('Enter', handleBlur);
    useKeyPress('Escape', handleBlur);

    const isTooltipEnabled =
      !!maxLength && value.length >= maxLength - 10 && value.length <= maxLength;
    const tooltipContent = `${value.length}/${maxLength}`;

    return isEditMode ? (
      <InputContainer data-input-type={variant}>
        <CharacterCountTooltip tooltipContent={tooltipContent} isTooltipEnabled={isTooltipEnabled}>
          <InlineEditAreaInput
            autoFocus={!EmptyTrigger || !!value}
            data-test={EDIT_INPUT_TEST_ID}
            hasError={hasError}
            id={id}
            isRequired={isRequired}
            maxLength={maxLength}
            onBlur={handleBlur}
            onChange={onChange}
            placeholder={placeholder}
            ref={ref}
            showFocused={showFocused}
            value={value}
          />
        </CharacterCountTooltip>
      </InputContainer>
    ) : (
      <>
        {EmptyTrigger && !value ? (
          <EmptyTrigger onClick={() => setIsEditMode(true)} />
        ) : (
          <StyledButtonArea
            data-input-type={variant}
            data-test={EDIT_BUTTON_TEST_ID}
            onClick={() => setIsEditMode(true)}
            variant={BUTTON_VARIANTS.GHOST}
          >
            <NameDisplayArea>
              {value || <NameDisplayPlaceholder>{placeholder}</NameDisplayPlaceholder>}
            </NameDisplayArea>
            <Edit16 />
          </StyledButtonArea>
        )}
      </>
    );
  },
);

export const FauxInlineEditArea = ({ value = '' }: FauxInlineEditProps) => {
  return (
    <StyledFauxButtonArea data-test={FAUX_EDIT_BUTTON_TEST_ID} variant={BUTTON_VARIANTS.GHOST}>
      <NameDisplayArea>{value}</NameDisplayArea>
    </StyledFauxButtonArea>
  );
};
